import { useState } from 'react'
import type { WebSocketHookReturn } from '../hooks/useWebSocket'
import { useSanityGateBuffer } from '../hooks/useSanityGateBuffer'
import { useExpandablePanel } from '../hooks/useExpandablePanel'
import { ExpandToggle } from './ExpandToggle'
import './LiveSanityGateFeed.css'

// 10/page mirrors WorkflowRunsFeed so the two cards sit at similar
// heights in the dash-zone-grid--2 row. The rolling buffer (100 events deep,
// 10 pages worth) lives in SanityGateBufferProvider.
const PAGE_SIZE = 10

// The `ws` prop is retained so callers are unchanged; events now come from the
// session-long buffer context (SanityGateBufferProvider).
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function LiveSanityGateFeed(_props: { ws: WebSocketHookReturn }) {
  const events = useSanityGateBuffer()
  const [page, setPage] = useState(1)
  // Defensive: if events shrinks (it currently only grows or caps, but be
  // robust against buffer-size changes), clamp page into range.
  const pages = Math.max(1, Math.ceil(events.length / PAGE_SIZE))
  const safePage = Math.min(page, pages)
  const visible = events.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
  const { expanded, toggle, scrollClassName } = useExpandablePanel('sanity-gate')
  return (
    <div className="card sanity-feed">
      <div className="sanity-feed-head">
        <h3>Live sanity-gate events</h3>
        <div className="sanity-feed-head-right">
          <span className="sanity-feed-total">{events.length} buffered</span>
          {events.length > 0 && (
            <ExpandToggle expanded={expanded} onToggle={toggle} />
          )}
        </div>
      </div>
      {events.length === 0 && (
        <p className="sanity-feed-empty">Waiting for live events…</p>
      )}
      {events.length > 0 && (
        <ul className={`sanity-feed-list ${scrollClassName}`}>
          {visible.map((e, i) => (
            <li key={(safePage - 1) * PAGE_SIZE + i} data-decision={e.decision}>
              <span className="sanity-feed-rule">{e.rule_name ?? '—'}</span>
              <span className="sanity-feed-sym">{e.symbol ?? '—'}</span>
              <span className="sanity-feed-dec">{e.decision ?? '—'}</span>
              <span className="sanity-feed-why">{e.reason_detail ?? ''}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="sanity-feed-pager">
        <button disabled={safePage <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
        <span>{safePage} / {pages}</span>
        <button disabled={safePage >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
      </div>
    </div>
  )
}
